import { useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { useAuditLogs } from "@/api/admin/audit-logs";
import { AdminErrorState } from "@/components/admin/AdminErrorState";

const PAGE: CSSProperties = {
  padding: "16px 24px 96px",
  display: "flex",
  flexDirection: "column",
  gap: 12,
};
const FILTER_ROW: CSSProperties = { display: "flex", gap: 8, flexWrap: "wrap" };
const INPUT: CSSProperties = {
  padding: "6px 10px",
  borderRadius: 999,
  border: "1px solid var(--d-line)",
  background: "var(--d-ink-2)",
  color: "var(--d-fg)",
  fontFamily: "var(--font-body)",
  fontSize: 13,
  minWidth: 160,
};
const TABLE: CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontFamily: "var(--font-mono)",
  fontSize: 12,
};
const TH: CSSProperties = {
  textAlign: "left",
  padding: "8px 6px",
  borderBottom: "1px solid var(--d-line)",
  color: "var(--d-fg-muted)",
  fontFamily: "var(--font-body)",
};
const TD: CSSProperties = {
  padding: "8px 6px",
  borderBottom: "1px solid var(--d-line)",
  verticalAlign: "top",
};
const PRE: CSSProperties = {
  margin: 0,
  fontSize: 11,
  color: "var(--d-fg-muted)",
  whiteSpace: "pre-wrap",
  fontFamily: "var(--font-mono)",
};

export function AuditLogPage(): JSX.Element {
  const { t } = useTranslation("admin");
  const [action, setAction] = useState("");
  const [adminTgId, setAdminTgId] = useState("");
  const { data, isLoading, isError, refetch } = useAuditLogs({
    limit: 200,
    action: action || undefined,
    adminTgId: adminTgId ? Number(adminTgId) : undefined,
  });

  if (isError) return <AdminErrorState onRetry={() => void refetch()} />;

  return (
    <div style={PAGE}>
      <h2 style={{ fontFamily: "var(--font-display)", fontSize: 22 }}>
        {t("audit_log.title")}
      </h2>
      <div style={FILTER_ROW}>
        <label>
          <span style={{ marginRight: 6, fontSize: 12, color: "var(--d-fg-muted)" }}>
            {t("audit_log.filter.action")}
          </span>
          <input
            aria-label={t("audit_log.filter.action")}
            style={INPUT}
            value={action}
            onChange={(e) => setAction(e.target.value)}
          />
        </label>
        <label>
          <span style={{ marginRight: 6, fontSize: 12, color: "var(--d-fg-muted)" }}>
            {t("audit_log.filter.admin_tg_id")}
          </span>
          <input
            aria-label={t("audit_log.filter.admin_tg_id")}
            style={INPUT}
            value={adminTgId}
            onChange={(e) => setAdminTgId(e.target.value.replace(/\D/g, ""))}
          />
        </label>
      </div>

      {isLoading && <div>{t("common.loading")}</div>}
      <table style={TABLE}>
        <thead>
          <tr>
            <th style={TH}>{t("audit_log.col.when")}</th>
            <th style={TH}>{t("audit_log.col.admin")}</th>
            <th style={TH}>{t("audit_log.col.action")}</th>
            <th style={TH}>{t("audit_log.col.payload")}</th>
          </tr>
        </thead>
        <tbody>
          {(data ?? []).map((row) => (
            <tr key={row.id}>
              <td style={TD}>
                {new Date(row.created_at).toISOString().replace("T", " ").slice(0, 19)}
              </td>
              <td style={TD}>{row.admin_tg_id ?? row.admin_user_id}</td>
              <td style={TD}>{row.action}</td>
              <td style={TD}>
                <details>
                  <summary style={{ cursor: "pointer", color: "var(--d-violet)" }}>
                    {t("audit_log.expand")}
                  </summary>
                  <pre style={PRE}>{JSON.stringify(row.payload, null, 2)}</pre>
                </details>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
